import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { updateAgent, type Agent } from './api';

// Mental model of the form: five sections, each maps to a group of
// policy fields the runner enforces server-side. The LLM can never
// bypass these — at most it sees ErrToolNotInvocable when it tries to
// exceed a cap and adapts.
type PerpetualsConfig = {
  allowed_markets?: string[];
  allowed_sides?: string[];
  max_leverage?: number;
  max_position_size_usd?: string;
  max_total_exposure_usd?: string;
  max_open_volume_per_day_usd?: string;
  require_stop_loss?: boolean;
  min_distance_to_liquidation_pct?: number;
  max_funding_rate_bps_against?: number;
  builder_code?: { address?: string; fee_tenths_bp?: number };
};

const DEFAULTS = {
  allowed_markets: ['BTC-USD', 'ETH-USD'],
  allowed_sides: ['long'],
  max_leverage: 3,
  max_position_size_usd: '1000',
  max_total_exposure_usd: '3000',
  max_open_volume_per_day_usd: '5000',
  require_stop_loss: true,
  min_distance_to_liquidation_pct: 5,
  max_funding_rate_bps_against: 10,
};

// Popular Hyperliquid perp markets — operators can add others via
// the free-text input but we offer the common ones as chips.
const COMMON_MARKETS = ['BTC-USD', 'ETH-USD', 'SOL-USD', 'AVAX-USD', 'ARB-USD', 'OP-USD', 'MATIC-USD', 'LINK-USD', 'ATOM-USD', 'TIA-USD'];

export function PerpetualsConfigForm({ orgId, agent }: { orgId: string; agent: Agent }) {
  const qc = useQueryClient();
  const cfg = (agent.config as PerpetualsConfig) ?? {};

  const [markets, setMarkets] = useState<string[]>(cfg.allowed_markets ?? DEFAULTS.allowed_markets);
  const [sides, setSides] = useState<string[]>(cfg.allowed_sides ?? DEFAULTS.allowed_sides);
  const [maxLeverage, setMaxLeverage] = useState(String(cfg.max_leverage ?? DEFAULTS.max_leverage));
  const [maxPosSize, setMaxPosSize] = useState(cfg.max_position_size_usd ?? DEFAULTS.max_position_size_usd);
  const [maxTotalExp, setMaxTotalExp] = useState(cfg.max_total_exposure_usd ?? DEFAULTS.max_total_exposure_usd);
  const [maxDayVol, setMaxDayVol] = useState(cfg.max_open_volume_per_day_usd ?? DEFAULTS.max_open_volume_per_day_usd);
  const [requireSL, setRequireSL] = useState(cfg.require_stop_loss ?? DEFAULTS.require_stop_loss);
  const [minLiqDist, setMinLiqDist] = useState(String(cfg.min_distance_to_liquidation_pct ?? DEFAULTS.min_distance_to_liquidation_pct));
  const [maxFundingAgainst, setMaxFundingAgainst] = useState(String(cfg.max_funding_rate_bps_against ?? DEFAULTS.max_funding_rate_bps_against));
  const [builderAddr, setBuilderAddr] = useState(cfg.builder_code?.address ?? '');
  const [builderFee, setBuilderFee] = useState(String(cfg.builder_code?.fee_tenths_bp ?? ''));
  const [newMarket, setNewMarket] = useState('');
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setMarkets(cfg.allowed_markets ?? DEFAULTS.allowed_markets);
    setSides(cfg.allowed_sides ?? DEFAULTS.allowed_sides);
    setMaxLeverage(String(cfg.max_leverage ?? DEFAULTS.max_leverage));
    setMaxPosSize(cfg.max_position_size_usd ?? DEFAULTS.max_position_size_usd);
    setMaxTotalExp(cfg.max_total_exposure_usd ?? DEFAULTS.max_total_exposure_usd);
    setMaxDayVol(cfg.max_open_volume_per_day_usd ?? DEFAULTS.max_open_volume_per_day_usd);
    setRequireSL(cfg.require_stop_loss ?? DEFAULTS.require_stop_loss);
    setMinLiqDist(String(cfg.min_distance_to_liquidation_pct ?? DEFAULTS.min_distance_to_liquidation_pct));
    setMaxFundingAgainst(String(cfg.max_funding_rate_bps_against ?? DEFAULTS.max_funding_rate_bps_against));
    setBuilderAddr(cfg.builder_code?.address ?? '');
    setBuilderFee(String(cfg.builder_code?.fee_tenths_bp ?? ''));
    setSaved(false);
    setErr(null);
  }, [agent.id]);

  const save = useMutation({
    mutationFn: () => {
      const lev = parseInt(maxLeverage, 10);
      if (Number.isNaN(lev) || lev < 1 || lev > 50) {
        throw new Error('max_leverage must be between 1 and 50');
      }
      const liqDist = parseFloat(minLiqDist);
      if (Number.isNaN(liqDist) || liqDist < 0 || liqDist > 100) {
        throw new Error('min_distance_to_liquidation_pct must be 0-100');
      }
      const funding = parseFloat(maxFundingAgainst);
      if (Number.isNaN(funding) || funding < 0) {
        throw new Error('max_funding_rate_bps_against must be ≥ 0');
      }
      const fee = builderFee.trim() === '' ? undefined : parseInt(builderFee, 10);
      if (fee !== undefined && (Number.isNaN(fee) || fee < 0 || fee > 50)) {
        throw new Error('builder fee must be 0-50 tenths of a bp');
      }
      const next: PerpetualsConfig = {
        allowed_markets: markets,
        allowed_sides: sides,
        max_leverage: lev,
        max_position_size_usd: maxPosSize.trim(),
        max_total_exposure_usd: maxTotalExp.trim(),
        max_open_volume_per_day_usd: maxDayVol.trim(),
        require_stop_loss: requireSL,
        min_distance_to_liquidation_pct: liqDist,
        max_funding_rate_bps_against: funding,
      };
      if (builderAddr.trim() !== '' && fee !== undefined) {
        next.builder_code = { address: builderAddr.trim(), fee_tenths_bp: fee };
      }
      return updateAgent(orgId, agent.id, { config: next });
    },
    onSuccess: () => {
      setSaved(true);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: Error) => setErr(e.message),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  function toggleMarket(m: string) {
    setMarkets((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }
  function addCustomMarket() {
    const m = newMarket.trim().toUpperCase();
    if (!m) return;
    const full = m.includes('-') ? m : `${m}-USD`;
    if (!markets.includes(full)) setMarkets([...markets, full]);
    setNewMarket('');
  }
  function toggleSide(s: string) {
    setSides((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  const isUsingSigilBuilder = builderAddr.trim() === '';

  return (
    <Card>
      <CardHeader
        title="Perpetuals policy"
        description="Caps and allow-lists for Hyperliquid perp positions. Every write tool runs through these gates server-side — the LLM can't exceed them. Live position state (PnL, liquidation price) always comes from the venue, not Sigil."
      />
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Section
            title="Markets"
            hint="Which perp pairs the agent may trade. Chip-select common pairs or add custom ones — Hyperliquid supports 100+ markets."
          >
            <div className="flex flex-wrap gap-2">
              {COMMON_MARKETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMarket(m)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    markets.includes(m)
                      ? 'border-bit2me bg-bit2me/10 text-bit2me'
                      : 'border-line-soft text-ink-secondary hover:bg-surface-soft'
                  }`}
                >
                  {m}
                </button>
              ))}
              {markets
                .filter((m) => !COMMON_MARKETS.includes(m))
                .map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMarket(m)}
                    className="rounded-full border border-bit2me bg-bit2me/10 px-3 py-1 text-xs text-bit2me"
                  >
                    {m} ×
                  </button>
                ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                value={newMarket}
                onChange={(e) => setNewMarket(e.target.value)}
                placeholder="WIF, FARTCOIN, …"
                className="rounded-md border border-line bg-surface-white px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={addCustomMarket}
                className="rounded-md border border-line-soft px-3 py-1 text-xs text-ink-secondary hover:bg-surface-soft"
              >
                Add
              </button>
            </div>
          </Section>

          <Section title="Sides" hint="Long-only is safer to reason about. Enable short only when you have a clear short strategy.">
            <div className="flex gap-2">
              {(['long', 'short'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSide(s)}
                  className={`rounded-md border px-4 py-1.5 text-sm capitalize ${
                    sides.includes(s)
                      ? 'border-bit2me bg-bit2me/10 text-bit2me'
                      : 'border-line-soft text-ink-secondary hover:bg-surface-soft'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Size caps">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max leverage" hint="1-50. Hyperliquid allows up to 50x; we recommend 3-5x for production agents.">
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={maxLeverage}
                  onChange={(e) => setMaxLeverage(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Max position size (USD)" hint="Notional cap per single open.">
                <input value={maxPosSize} onChange={(e) => setMaxPosSize(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Max total exposure (USD)" hint="Sum of all open positions on the venue.">
                <input value={maxTotalExp} onChange={(e) => setMaxTotalExp(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Max opens / day (USD)" hint="Rolling-24h cap on new opens. Closes don't count.">
                <input value={maxDayVol} onChange={(e) => setMaxDayVol(e.target.value)} className={inputCls} />
              </Field>
            </div>
          </Section>

          <Section title="Risk guards">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={requireSL}
                onChange={(e) => setRequireSL(e.target.checked)}
                className="mt-1 accent-bit2me"
              />
              <div>
                <p className="text-sm text-ink-primary">Require stop-loss on every open</p>
                <p className="mt-0.5 text-xs text-ink-tertiary">Strongly recommended. Refuses opens without a SL price.</p>
              </div>
            </label>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field
                label="Min distance to liquidation (%)"
                hint="Refuse to open if the computed liquidation price would land within this percentage of the current price. 0 disables."
              >
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={minLiqDist}
                  onChange={(e) => setMinLiqDist(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field
                label="Max funding rate against you (bps)"
                hint="Refuse to open long when funding is paying > X bps/hr (you'd pay); equivalent short. 0 disables."
              >
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={maxFundingAgainst}
                  onChange={(e) => setMaxFundingAgainst(e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Builder code"
            hint="Hyperliquid forwards a trading-fee revenue share to this address on every order. Leave empty to use Sigil's platform default — when you set your own, you keep 100% of the builder revenue your agent generates."
          >
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-ink-secondary">Builder address</label>
                <input
                  value={builderAddr}
                  onChange={(e) => setBuilderAddr(e.target.value)}
                  placeholder="0x… (empty = Sigil default)"
                  className={inputCls + ' font-mono text-xs'}
                />
              </div>
              <div>
                <label className="block text-xs text-ink-secondary">Fee (tenths of a bp)</label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={builderFee}
                  onChange={(e) => setBuilderFee(e.target.value)}
                  placeholder="5"
                  className={inputCls}
                />
              </div>
            </div>
            <p
              className={`mt-2 text-[11px] ${
                isUsingSigilBuilder ? 'text-amber-700' : 'text-ok'
              }`}
            >
              {isUsingSigilBuilder
                ? 'Using Sigil platform builder code. Revenue goes to Sigil.'
                : 'Using your own builder code. Revenue goes to your address.'}
            </p>
          </Section>

          {err && <p className="text-sm text-danger">{err}</p>}
          <div className="flex items-center justify-end gap-3">
            {saved && <p className="font-mono text-xs text-ok">Saved</p>}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

const inputCls =
  'mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me';

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-ink-primary">{title}</label>
      {hint && <p className="mt-0.5 text-xs text-ink-tertiary">{hint}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-ink-secondary">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[10px] text-ink-tertiary">{hint}</p>}
    </div>
  );
}
